import React from 'react';
import {Car, Calendar, Hash} from 'lucide-react';
import {formatIsActive, formatLicensePlate} from '@/utils/formatters';
import type {Car as CarType} from '@/types';
import StatusBadge from "@/components/UI/StatusBadge.tsx";

interface CarsSectionProps {
    cars: CarType[];
}

export const CarsSection: React.FC<CarsSectionProps> = ({cars}) => {
    if (cars.length === 0) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center">
                    <Car className="mx-auto h-12 w-12 text-gray-400"/>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        This user has no registered vehicles.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Car className="h-6 w-6 text-gray-400"/>
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                    Registered Vehicles
                                </dt>
                                <dd className="text-lg font-medium text-gray-900">
                                    {cars.length}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cars Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cars.map((car) => (
                    <div key={car.id} className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center">
                                    <Car className="h-6 w-6 text-gray-400 mr-2"/>
                                    <h4 className="text-lg font-medium text-gray-900">
                                        {car.make} {car.car_model}
                                    </h4>
                                </div>
                                <StatusBadge status={formatIsActive(car.is_active)}/>
                            </div>

                            {/* Vehicle Details */}
                            <div className="space-y-3">
                                {/* License Plate */}
                                <div className="flex items-center">
                                    <Hash className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0"/>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">
                                            {formatLicensePlate(car.number)}
                                        </p>
                                        <p className="text-xs text-gray-500">License Plate</p>
                                    </div>
                                </div>

                                {/* Year */}
                                {car.year &&
                                    <div className="flex items-center">
                                        <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0"/>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {car.year}
                                            </p>
                                            <p className="text-xs text-gray-500">Year</p>
                                        </div>
                                    </div>
                                }

                                {/* VIN if available */}
                                {car.vin && (
                                    <div className="flex items-start">
                                        <Hash className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5"/>
                                        <div>
                                            <p className="text-sm font-mono text-gray-900 break-all">
                                                {car.vin}
                                            </p>
                                            <p className="text-xs text-gray-500">VIN</p>
                                        </div>
                                    </div>
                                )}

                                {/* Insurance Expiry if available */}
                                {car.insurance_expiry && (
                                    <div className="flex items-center">
                                        <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0"/>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {new Date(car.insurance_expiry).toLocaleDateString()}
                                            </p>
                                            <p className="text-xs text-gray-500">Insurance Expires</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">Vehicle ID</span>
                                    <span className="text-xs font-medium text-gray-900">#{car.id}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CarsSection;
